/** @module csi/action/ImageReplaceSelect */
define([
	"react",
	"react-dom/client",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"csi/action",
	"csi/action/_ImageBrowseMixin",
	"csi/action/Controller",
	"csi/comm/Aspera",
	"designSystem/components/icon/TurntableIcon",
	"csi/def/Action",
	"csi/html",
	"csi/html/Widget",
	"csi/image/_base",
	"csi/locale",
	"csi/model/image",
	"csi/property/ImageBrowse",
	"csi/reflection",
	"csi/service/fileService",
	"csi/state/rootStore",
	"csi/widget/_DndHTML5Mixin",
	"csi/wizard/StepData",
	"csi/xml",
	"dojo/text!SiteAdmin/templates/ImageReplaceSelect.html",
	"csi/page/localeProvider",
], function(
	React,
	ReactDom,
	declare,
	lang,
	csiAction,
	_ImageBrowseMixin,
	Controller,
	Aspera,
	TurntableIcon,
	Action,
	html,
	Widget,
	csiImage,
	locale,
	{isTurntableImage},
	ImageBrowse,
	reflection,
	fileService,
	{ sessionStore },
	_DndHTML5Mixin,
	StepData,
	xml,
	template,
	localeProvider
) {
	var _ImageReplaceBrowseController = declare([Controller, _ImageBrowseMixin], {
		getTitle: function() {
			return locale.resource("BrowseImage");
		},

		preProcess: function() {
			// determine if selecting one or many images
			this.bOneImage = true;
			this.bAllowSelection = true;
			this.inherited(arguments);
		},

		onBrowserSave: function(nodes) {
			// Show new image in "InlineReplaceSelect" widget
			if (nodes && nodes.length) {
				var node = nodes[0];
				this.widget.setImageSrc(csiImage.getImageSrc(node, null, reflection.ATTR_MEDIUM_IMAGE), node.$Name);
			}
		},
	});

	const _AsperaController = declare(Controller, {
		process() {
			let options = {
				allowedFileTypes: [
					{
						filter_name: locale.resource("Image"),
						extensions: ["png", "jpg", "jpeg", "gif", "bmp"],
					},
				],
			};
			this.waitForPublish(Aspera.uploadFile(sessionStore.userPreferencesUrl, "", false, false, null, options));
		},
		postProcessSuccess() {
			const node = this.m_response.getResultNode();
			this.widget._onPostUploadAspera(node);
		},
	});

	return declare([Widget, _DndHTML5Mixin], {
		templateString: template,
		bShowFinishButton: false,

		/** @property {boolean} bTurntable - true if we are uploading turntable zip file*/
		bTurntable: false,

		/** @property {string} oldImageUrl - CNL of old image */
		oldImageUrl: "",

		/** @property {string} newImageUrl - URL of new image */
		newImageUrl: "",

		/** @property {StepData} stepData - holds the step information */
		stepData: null,

		/** @property {Object[]} _files - array of file object(s) to store values from file browse dialog. */
		_files: null,

		/** @property {File} _dndFile - used internally by this widget */
		_dndFile: null,

		onViewStart: function(dfds) {
			this.inherited(arguments);
			this.stepData = new StepData({type: StepData.WIZARD_IMAGE});

			var ctx = this.getContext();
			var node = ctx.getNode();
			var bImage = node[xml.ATTR_TYPE] == reflection.TYPE_IMAGE;
			if (!bImage) {
				this.oldImageUrl = this.getSourceController().getAction().m_viewRef.m_url;
				node = ctx.cache.getNode(this.oldImageUrl);
			} else {
				this.oldImageUrl = node.$URL;
			}
			var fileName = node.$Name;
			html.text(this.oldImageNameNode, fileName);
			this.oldImageNameNode.title = fileName;

			this.oldImageNode.src = csiImage.getImageSrc(node, null, reflection.ATTR_MEDIUM_IMAGE);

			// If the old image is a turntable, then show the turn table icon
			if (isTurntableImage(node)) {
				const root = ReactDom.createRoot(this.turntableIconNodeOld);
				root.render(React.createElement(TurntableIcon.default));
				
				Object.assign(this.turntableIconNodeOld.style, {
					bottom: 0,
					display: "inline",
					position: "absolute",
					right: "5px",
				});
			}
		},

		/**
		 * Image Drag Drop handler
		 * @param {File[]} files - list of dropped files
		 */
		_onDropImage: function(files) {
			this._dndFile = files[0];
			this._setDndFile(null);
		},

		/**
		 * Setup new file image
		 * @param {String} src
		 * @param {String} alt
		 */
		setImageSrc: function(src, alt) {
			this.newImageNode.src = src;
			this.newImageNode.alt = alt;
			this.newImageNode.title = alt;
			this.imageDropMsgNode.innerText = "";
		},

		/**
		 * Toggle ON/Off Drag and Drop and Paste functionality
		 * @param {boolean} active
		 */
		toggleDnD: function(bActive) {
			this.dndHTML5Toggle(bActive, this.imageDropNode, this._onDropImage, null, true, true);
		},

		/**
		 * Image selected via Image Browser
		 * @param {_ImageReplaceBrowseController} ac
		 */
		_onPostBrowse: function(ac) {
			if (ac._imageUrls.length) {
				this.newImageUrl = ac._imageUrls[0];
				this._files = null;

				const imageNode = ac.m_cache.getNode(this.newImageUrl);
				this.bTurntable = isTurntableImage(imageNode);
				const newImgName = imageNode.$Name;
				this.stepData.setName(newImgName);
				this._showNewFileName(newImgName);
			}
		},

		/**
		 * Click on drop down menu item "Select From Image Browser"
		 */
		_onImageBrowserClick: function() {
			csiAction.invoke(
				this,
				null,
				lang.mixin(new ImageBrowse(), {m_id: "ReplaceImage", m_postAction: "_onPostBrowse", classController: _ImageReplaceBrowseController}),
				{}
			);
		},

		/**
		 * Forward UploadButton click to hidden input type=file;
		 */
		_onUploadClick: function() {
			if (Aspera.isEnabled()) {
				csiAction.invoke(this, null, lang.mixin(new Action(), {classController: _AsperaController}), {});
			} else {
				this.inputFileNode.click();
			}
		},

		/**
		 * User selected new file for upload
		 * @param {Event} [ev]
		 */
		_setDndFile: function(ev) {
			// function triggered by input control change, so in _onPostUploadSelect() we'll use input.files instead of _dndFiles
			if (ev) {
				this._dndFile = null;
			}
			csiAction.invoke(this, null, lang.mixin(new Action(), {m_postAction: "_onPostUploadSelect"}), {});
		},

		/**
		 * Callback for the action above - new file selected for upload
		 * This will display selected image, file name and will also remember user selection
		 * @param {Controller} ac
		 */
		_onPostUploadSelect: function(ac) {
			var input = this.inputFileNode;
			var newFile = this._dndFile || (input.files && input.files[0]); // Use files from Drag Drop or from input control
			if (newFile) {
				this._files = [newFile];
				// If in IE the name is not provided then try to use image type to generate valid name
				var fileName = fileService.getFileName(newFile);
				this.stepData.setName(fileName);

				if (fileService.isZipFileName(fileName)) {
					this._parseZip(ac, fileName, newFile);
				} else {
					this._parseImage(ac, fileName, newFile);
				}
			}
			input.value = "";
		},

		/**
		 * Validate turntable file and update UI with file name and image
		 * @param {Controller} ac
		 * @param {string} fileName
		 * @param {File} newFile
		 */
		_parseZip: function(ac, fileName, newFile) {
			fileService.validateZipIsTurntable(newFile, true, localeProvider).then(
				(imgUrl) => {
					this.bTurntable = true;
					this._showNewFileName(fileName);
					this.setImageSrc(imgUrl, fileName);
					this.newImageUrl = "";
				},
				(err) => {
					validTurntable = false;
					ac.displayErrorDialog({
						message: locale.resource("ErrorInvalidFiles",[locale.resource(err.message)]),
					});
				}
			);
		},

		/**
		 * Validate image file and update UI with file name and image
		 * @param {Controller} ac
		 * @param {string} fileName
		 * @param {File} newFile
		 */
		_parseImage: function(ac, fileName, newFile) {
			fileService.validateImageFile(newFile).then(
				(res) => {
					this.bTurntable = false;
					this._showNewFileName(fileName);
					this.setImageSrc(res, fileName);
					this.newImageUrl = "";
				},
				() => {
					ac.displayErrorDialog({
						suffix: "_ErrorImage",
					});
				}
			);
		},

		/**
		 * Show new file name
		 * @param {string} fileName
		 */
		_showNewFileName: function(fileName) {
			html.setInnerText(this.newImageNameNode, fileName, true);
			this.newImageNameNode.title = fileName;
			const bTurntable = this.bTurntable;

			if (bTurntable && !this.turntableIconNode.children.length) {
				const root = ReactDom.createRoot(this.turntableIconNode);
				root.render(React.createElement(TurntableIcon.default));
			}

			html.setVisible(this.turntableIconNode, bTurntable);
		},

		_onPostUploadAspera: function(node) {
			if (node) {
				// If in IE the name is not provided then try to use image type to generate valid name
				this.stepData.setName(node.$Name);

				if (csiImage.isValidFormat(node.$Name)) {
					// Show new file name
					html.setInnerText(this.newImageNameNode, node.$Name, true);
					this.setImageSrc(csiImage.getImageSrc(node, null, reflection.ATTR_MEDIUM_IMAGE), node.$Name);
					this.newImageUrl = node.$URL;
				}
			}
		},

		destroy: function() {
			this.dndHTML5Destroy();
			this.inherited(arguments);
		},
	});
});
